import React from "react";
import { Link } from "@tanstack/react-router";
import { differenceInDays } from "date-fns";
import {
  ClipboardList,
  Crown,
  FileText,
  LayoutDashboard,
  LogOut,
  Package,
  Receipt,
  Settings,
  TrendingUp,
  Tv,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { CompanySwitcher } from "@/components/CompanySwitcher";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/AuthProvider";

export function SidebarLink({
  to,
  icon,
  label,
  exact = false,
  onClick,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  exact?: boolean;
  onClick?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      activeOptions={{ exact }}
      className="flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-slate-300 hover:text-white hover:bg-slate-800"
      activeProps={{
        className:
          "bg-blue-600/10 text-blue-400 font-medium border-r-2 border-blue-500",
      }}
    >
      {icon}
      <span>{label}</span>
    </Link>
  );
}

export function Sidebar({
  isSidebarOpen,
  setIsSidebarOpen,
  hasPublicidade,
  subscription,
}: {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (val: boolean) => void;
  hasPublicidade: boolean;
  subscription: any;
}) {
  const { user } = useAuth();
  const role = user?.user_metadata?.role || "COLABORADOR";
  const isSuperadmin = role === "SUPERADMIN";
  const isAdmin = role === "ADMIN" || isSuperadmin;
  const isCaixa = role === "CAIXA";
  const isAtendente = role === "ATENDENTE";

  return (
    <aside
      className={`
      fixed inset-y-0 left-0 z-50 w-64 border-r border-slate-800 bg-slate-900 flex flex-col transition-transform duration-300 ease-in-out
      lg:static lg:translate-x-0
      ${isSidebarOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}
    `}
    >
      <div className="p-4 border-b border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/logo.webp" className="w-8 h-8 object-contain" alt="Logo" />
          <h2 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-blue-200 bg-clip-text text-transparent">
            AutoWash Pro
          </h2>
        </div>
        <button
          onClick={() => setIsSidebarOpen(false)}
          className="text-slate-400 hover:text-white lg:hidden p-1"
        >
          <X size={18} />
        </button>
      </div>

      <div className="p-3 border-b border-slate-800">
        <CompanySwitcher />
      </div>

      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {isSuperadmin && (
          <div className="mb-4">
            <Link
              to="/master"
              onClick={() => setIsSidebarOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-3 py-2.5 bg-gradient-to-r from-amber-500/10 to-amber-600/10 text-amber-500 hover:from-amber-500/20 hover:to-amber-600/20 rounded-lg transition-all border border-amber-500/20 hover:border-amber-500/40 shadow-lg shadow-amber-500/5"
            >
              <Crown size={16} />
              <span className="font-bold text-sm tracking-wide">
                Voltar ao Master HQ
              </span>
            </Link>
          </div>
        )}

        <SidebarLink
          to="/app"
          icon={<LayoutDashboard size={18} />}
          label="Recepção e Pátio"
          exact
          onClick={() => setIsSidebarOpen(false)}
        />
        <SidebarLink
          to="/app/quotes"
          icon={<FileText size={18} />}
          label="Orçamentos"
          onClick={() => setIsSidebarOpen(false)}
        />
        <SidebarLink
          to="/app/tv"
          icon={<Tv size={18} />}
          label="Painel TV (Pátio)"
          onClick={() => setIsSidebarOpen(false)}
        />

        {hasPublicidade && (
          <SidebarLink
            to="/app/tv-midia"
            icon={<Crown size={18} />}
            label="Publicidade em Tela"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {(isAdmin || isCaixa) && (
          <SidebarLink
            to="/app/financial"
            icon={<Receipt size={18} />}
            label="Caixa"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {(isAdmin || isAtendente || isCaixa) && (
          <>
            <SidebarLink
              to="/app/customers"
              icon={<Users size={18} />}
              label="Clientes e Veículos"
              onClick={() => setIsSidebarOpen(false)}
            />
            <SidebarLink
              to="/app/services"
              icon={<Wrench size={18} />}
              label="Catálogo de Serviços"
              onClick={() => setIsSidebarOpen(false)}
            />
          </>
        )}

        {(isAdmin || isCaixa) && (
          <SidebarLink
            to="/app/inventory"
            icon={<Package size={18} />}
            label="Estoque"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {(isAdmin || isAtendente) && (
          <SidebarLink
            to="/app/surveys"
            icon={<ClipboardList size={18} />}
            label="Pesquisas (NPS)"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {isAdmin && (
          <>
            <div className="pt-4 pb-2">
              <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Gestão e Growth
              </p>
            </div>
            <SidebarLink
              to="/app/growth"
              icon={<TrendingUp size={18} />}
              label="AI Growth e histórico financeiro"
              onClick={() => setIsSidebarOpen(false)}
            />
            <SidebarLink
              to="/app/team"
              icon={<Users size={18} />}
              label="Equipe e Comissões"
              onClick={() => setIsSidebarOpen(false)}
            />
            <SidebarLink
              to="/app/settings"
              icon={<Settings size={18} />}
              label="Configurações"
              onClick={() => setIsSidebarOpen(false)}
            />
          </>
        )}
      </nav>

      <div className="p-4 border-t border-slate-800 flex flex-col gap-3">
        {subscription && (
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold ${
              subscription.current_period_end &&
              new Date(subscription.current_period_end) < new Date()
                ? "bg-red-500/10 text-red-400 border border-red-500/20"
                : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
            }`}
          >
            <Crown size={14} />
            <div className="flex-1 truncate">
              {subscription.system_plans?.name || "Assinante"}
            </div>
            {subscription.current_period_end && (() => {
              const diff = differenceInDays(
                new Date(subscription.current_period_end),
                new Date()
              );
              return (
                <div className="opacity-80 font-mono">
                  {diff < 0 ? "Vencido" : `${diff}d`}
                </div>
              );
            })()}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-sm font-medium">
              {user?.email?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium leading-none truncate w-32">
                {user?.email}
              </span>
              <span className="text-xs text-slate-400 mt-1">{role}</span>
            </div>
          </div>
          <button
            onClick={() => supabase.auth.signOut()}
            className="text-slate-400 hover:text-red-400 transition-colors"
            title="Sair"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </aside>
  );
}
